<template>
  <div
    ref="audioPlayer"
    class="audio-player flex flex-align-center">
    <SBIcon
      :icon="audioPreviewPlaying ? SBIconList.PAUSE : SBIconList.PLAY"
      :size="10"
      :pointer="true"
      @click="toggleAudio" />
    <div
      ref="progressWrapper"
      class="progress-wrapper greedy"
      :class="{disabled: !duration}"
      :style="progressStyle"
      @mousedown="seek"
      @mousemove="seek">
      <b-progress
        :value="currentAudioPercentage" />
    </div>
    <span class="time">{{ currentAudioTime | playerTimeFormat }} <span v-if="duration">/ {{ duration | playerTimeFormat }}</span></span>
    <!-- <span @click="download(url)">Down</span> -->
  </div>
</template>

<script>
// import getEncryptedApiKey from '@/helpers/getEncryptedApiKey';
// import Axios from 'axios';
import SBIcon from '@/components/common/Icon';
import SBIconList from '@/helpers/lists/sb-icon-list';
import SBColors from '@/helpers/lists/colors';

/**
 * Component for playing audio files.
 */
export default {
  name: 'SBAudioPlayer',
  components: {
    SBIcon,
  },
  props: {
    type: {
      type: String,
      required: false,
      default: 'mp4',
    },
    name: {
      type: String,
      required: false,
      default: 'No name',
    },
    url: {
      type: String,
      required: true,
    },
    art: {
      type: String,
      required: false,
      default: '',
    },
    downloadable: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      audio: null,
      audioPreviewPlaying: false,
      duration: null,
      currentAudioTime: 0,
      currentAudioPercentage: 0,
      progressStyle: {
        '--progress-percentage': '0%',
      },
      SBColors,
      SBIconList,
    };
  },
  watch: {
    url() {
      this.loadAudio();
    },
  },
  mounted() {
    this.loadAudio();
    this.audio.addEventListener('canplay', this.setDuration);
    this.audio.addEventListener('ended', this.audioEnded);
    this.audio.addEventListener('timeupdate', this.audioTimeUpdate);
  },
  beforeDestroy() {
    this.audio.pause();
    this.audio.removeEventListener('canplay', this.setDuration);
    this.audio.removeEventListener('ended', this.audioEnded);
    this.audio.removeEventListener('timeupdate', this.audioTimeUpdate);
  },
  methods: {
    download(url) {
      // console.log(`Download ${url}`);
      // Download attribute on "a" tag doesn't work any more for remote files.
      // The workaround is to download the file to the user memory and then create local url which can then be "downloaded" to file system.
      // he only issue with the current setup is that the cors blocks downloading of a file even thou it can be accessed from the browser.
      // https://stackoverflow.com/questions/27429123/html5-audio-web-audio-api-cors-and-firefox

      // Here is the more or less finished solution once the cors is enabled with special headers
      // Or if cors will be disabled for static files then just remove all the headers config below and use the solution.
      // ===============================================
      // let headers = {};
      // const token = localStorage.getItem('pbdToken');
      // if (process.env.NODE_ENV === 'development') {
      //   const devHeader = process.env.VUE_APP_DEV_HEADER.split(':');
      //   headers[devHeader[0]] = devHeader[1];
      // }
      // Axios({
      //       url: url,
      //       method: 'GET',
      //       responseType: 'blob',
      //       withCredentials: false,
      //       headers: {
      //         Authorization: `Bearer ${token}`,
      //         Accept: 'application/json',
      //         'X-Authorization': getEncryptedApiKey(),
      //         ...headers,
      //       },
      //   }).then((response) => {
      //     var fileURL = window.URL.createObjectURL(new Blob([response.data]));
      //     var fileLink = document.createElement('a');

      //     fileLink.href = fileURL;
      //     fileLink.setAttribute('download', 'file.pdf');
      //     document.body.appendChild(fileLink);
      //     fileLink.click();
      //   });
    },
    /** Resets the play icon when audio track reaches the end. */
    audioEnded() {
      this.audioPreviewPlaying = false;
    },
    /** Updates the player UI when audio time is changed. */
    audioTimeUpdate() {
      this.currentAudioTime = Math.floor(this.audio.currentTime);
      // If duration was set as infinite on the first load then update the duration as soon as is becomes available
      if (Number.isFinite(this.audio.duration)) {
        this.duration = this.audio.duration;
        this.currentAudioPercentage = this.audio.currentTime / this.audio.duration * 100;
        this.progressStyle = {
          '--progress-percentage': `${this.currentAudioPercentage}%`,
        };
      }
    },
    /** loads audio on component mount or url prop change. */
    loadAudio() {
      this.audioPreviewPlaying = false;
      this.currentAudioTime = 0;
      this.currentAudioPercentage = 0;
      this.progressStyle = {
        '--progress-percentage': '0%',
      };

      if (this.audio) {
        this.audio.pause();
        this.audio.src = this.url;
      } else {
        this.audio = new Audio(this.url);
      }

      this.audio.load();
    },
    /** Checks the duration of a loaded audio file and sets the reactive variable for the template to its value. */
    setDuration(event) {
      this.duration = Number.isFinite(this.audio.duration) ? Math.round(this.audio.duration) : null;
    },
    /** Toggles audio playback. */
    toggleAudio() {
      if (this.audioPreviewPlaying) {
        this.audio.pause();
        this.audioPreviewPlaying = false;
      } else {
        this.audio.play();
        this.audioPreviewPlaying = true;
      }
    },
    /**
     * Triggers when user clicks on the audio progress bar.
     *
     * @param event Mouse event.
     */
    seek(event) {
      // Do not seek if left click is not active
      if (event.which !== 1) {
        return;
      }
      // const wrapperElement = event.path.find(element => element.className === 'progress-wrapper');
      const wrapperElement = event.srcElement;
      const x = Math.abs(event.offsetX < 0 ?
        0 : wrapperElement && event.offsetX > wrapperElement.clientWidth ?
          wrapperElement.clientWidth : event.offsetX);
      const setProgress = Math.floor(x / wrapperElement.clientWidth * 100);
      const setCurrentTime = this.duration ? this.duration * setProgress / 100 : 0;
      this.currentAudioTime = Math.floor(setCurrentTime);
      this.audio.currentTime = setCurrentTime;
      // DO not use setProgress calculated sbove because when we clculate the percentage time from that value there will be a little diffence
      // due to the percentage calculations and rounding and the progress bar will almost always backup a pixel or two when we sart playing.
      // Because of thet recalculate the exact percentage again based on the current time set.
      this.currentAudioPercentage = this.duration ? this.audio.currentTime / this.audio.duration * 100 : 0;
      this.progressStyle = {
        '--progress-percentage': `${this.currentAudioPercentage}%`,
      };
    },
  },
};
</script>

<style lang="scss" scoped>
.audio-player {
  color: $blackPrimary;
  background: $greyBorder;
  max-width: 400px; // TODO try to return to 300 once the download button is implemented
  font-size: 14px;
  border-radius: 50px;
  padding: 8px 20px;
  overflow: hidden;

  span {
    cursor: default;
  }

  .time {
    min-width: 100px; // Added so the different fonts do not cause the player to expand/shrink on time change
    text-align: right;
  }

  ::v-deep .progress-wrapper {
    margin: 0 0 0 20px;
    min-width: 50px;
    height: 14px;
    display: flex;
    align-items: center;
    cursor: pointer;
    position: relative;

    &.disabled {
      opacity: 0.3;
      cursor: default;
    }

    .progress {
      min-width: 50px;
      background-color: $blackUltralight;
      height: 5px;
      border-radius: 5px;

      &::after {
        content: "";
        display: block;
        position: absolute;
        top: 2px;
        left: calc(var(--progress-percentage) - 5px);
        height: 10px;
        width: 10px;
        background: $blackPrimary;
        border-radius: 10px;
      }

      .progress-bar {
        background-color: $blackPrimary;
        transition: unset !important;
      }
    }
  }
}
</style>
